<script setup>
const props = defineProps({
  icon: {
    type: String,
    default: '',
  },
  iconPos: {
    type: String,
    default: 'left',
    validator: (val) => ['left', 'right'].includes(val),
  },
  label: {
    type: String,
    default: '',
  },
  severity: {
    type: String,
    default: 'primary',
    validator: (val) => ['primary', 'secondary', 'success', 'warning', 'danger', 'white'].includes(val),
  },
  size: {
    type: String,
    default: 'md',
    validator: (val) => ['sm', 'md', 'lg'].includes(val),
  },
  variant: {
    type: String,
    default: 'default',
    validator: (val) => ['default', 'outlined', 'text'].includes(val),
  },
  responsive: {
    type: Boolean,
    default: false,
  },
  rounded: {
    type: Boolean,
    default: false,
  },
  linkTo: {
    type: Object,
    default: null,
  },
});

const severitySwitch = (severity) => {
  switch (severity) {
    case 'primary': return { primary: 'primary', secondary: 'primary-400' }
    case 'secondary': return { primary: 'primary-400', secondary: 'white' }
    case 'success': return { primary: 'success', secondary: 'white' }
    case 'warning': return { primary: 'warning', secondary: 'white' }
    case 'danger': return { primary: 'danger', secondary: 'white' }
    case 'info': return { primary: 'info', secondary: 'white' }
    case 'white': return { primary: 'white', secondary: 'primary' }
  }
};

const sizeSwitch = (size) => {
  switch (size) {
    case 'lg': return props.rounded ? 'p-3 text-md gap-md' : 'py-3 px-6 text-md gap-md'
    case 'md': return props.rounded ? 'p-2 text-md gap-md' : 'py-2 px-5 text-md gap-md'
    case 'sm': return props.rounded ? 'p-2 text-sm gap-sm' : 'py-2 px-4 text-sm gap-sm'
    default: return 'py-2 px-5'
  }
};
</script>

<template>
  <RouterLink
    v-if="linkTo"
    :to="linkTo"
    :class="[
      {'border-0' : variant !== 'outlined'},
      'rounded-pill font-medium flex items-center no-decoration',
      variant === 'default' ? `bg-${severitySwitch(severity).primary}` : 'bg-transparent',
      variant === 'outlined' ?? `border-${severitySwitch(severity).primary}`,
      (variant === 'outlined' || variant === 'text') ? `text-${severitySwitch(severity).primary}` : `text-${severitySwitch(severity).secondary}`,
      sizeSwitch(size),
    ]"
  >
    <i
      v-if="iconPos === 'left' && icon"
      :class="icon"
    />
    <span v-if="label">{{ label }}</span>
    <i
      v-if="iconPos === 'right' && icon"
      :class="icon"
    />
  </RouterLink>
  <button
    v-else
    type="button"
    :class="[
      {'border-0' : variant !== 'outlined'},
      'rounded-pill font-medium flex items-center',
      variant === 'default' ? `bg-${severitySwitch(severity).primary}` : 'bg-transparent',
      variant === 'outlined' ?? `border-${severitySwitch(severity).primary}`,
      (variant === 'outlined' || variant === 'text') ? `text-${severitySwitch(severity).primary}` : `text-${severitySwitch(severity).secondary}`,
      sizeSwitch(size),
    ]"
  >
    <i
      v-if="iconPos === 'left' && icon"
      :class="icon"
    />
    <span v-if="label">{{ label }}</span>
    <i
      v-if="iconPos === 'right' && icon"
      :class="icon"
    />
  </button>
</template>

<style lang="scss" scoped>
.gap {
  &-md {gap: 8px;}
  &-sm {gap: 6px;}
}
</style>